import { Box, Typography } from "@mui/material";
import useColors from "api/hooks/useColors";
import TextButton from "components/buttons/TextButton";
import CustomTooltip from "components/CustomTooltip";
import SearchInput from "components/filters/SearchInput";
import StyledDataGrid from "components/StyledDataGrid";
import { useState } from "react";
import IntegrationImage from "scenes/stack/integrations/components/IntegrationImage";

const ToolsTableWidget = ({ integrations, isLoading }) => {
  const colors = useColors();
  const [appliedFilters, setAppliedFilters] = useState({
    searchString: "",
  });

  const handleClearFilters = () => {
    setAppliedFilters({
      searchString: "",
    });
  };

  const filteredTools = (() => {
    if (!integrations) return [];
    let result = [...integrations];

    if (appliedFilters.searchString) {
      const search = appliedFilters.searchString.toLowerCase();
      result = result.filter(
        (t) =>
          t.integration.toLowerCase().includes(search) ||
          t.customers.some((c) => c.toLowerCase().includes(search))
      );
    }

    return result;
  })();

  const integrationColumns = [
    {
      field: "integration",
      headerName: "Tool",
      flex: 1,
      renderCell: (params) => {
        const toolName = params.value?.toLowerCase().replace(/ /g, "_");
        return (
          <Box display="flex" justifyContent="center" alignItems="center">
            <IntegrationImage
              name={toolName}
              width="28px"
              showToolTip={true}
              newLogo={true}
            />
          </Box>
        );
      },
    },
    { field: "customer_count", headerName: "Customer Count", flex: 0.5 },
    {
      field: "customers",
      headerName: "Customers",
      flex: 2,
      renderCell: (params) => {
        const customerList = params.value || [];
        const displayCustomers = customerList.slice(0, 5);
        const remaining = customerList.length - 5;

        return (
          <Box>
            <Typography variant="caption">
              {displayCustomers.join(", ")}
              {remaining > 0 && (
                <CustomTooltip title={customerList.slice(5).join(", ")}>
                  <span style={{ color: colors.text_secondary }}>
                    {" "}
                    +{remaining} more
                  </span>
                </CustomTooltip>
              )}
            </Typography>
          </Box>
        );
      },
    },
  ];

  return (
    <Box
      sx={{ height: "100%", display: "flex", flexDirection: "column", gap: 2 }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: 1.5,
          alignItems: "center",
          padding: "8px 0",
          borderBottom: `1px solid ${colors.divider2}`,
        }}
      >
        <Box sx={{ flex: 1 }}>
          <SearchInput
            fieldName="searchString"
            appliedFilters={appliedFilters}
            setAppliedFilters={setAppliedFilters}
            placeholder="Search tools or customers..."
          />
        </Box>
        <Box>
          <TextButton onClick={handleClearFilters} size="small">
            Clear
          </TextButton>
        </Box>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0 }}>
        <StyledDataGrid
          rows={filteredTools.map((integration, idx) => ({
            id: idx,
            ...integration,
          }))}
          columns={integrationColumns}
          loading={isLoading}
          disableRowSelectionOnClick
          pageSizeOptionsOverride={[10, 25, 50]}
          autoHeight={false}
          boxSXProps={{ height: "100%" }}
        />
      </Box>
    </Box>
  );
};

export default ToolsTableWidget;
